import {
  type ComponentProps,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useReducer,
  useRef
} from 'react';
import { animate, motion, useInView } from 'framer-motion';
import { darken, rgba } from 'polished';
import styled, { css } from 'styled-components';

import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import {
  backgroundImageHatched,
  CheckeredMargin,
  cssDotGrid,
  gridColorSelector,
  GridRule
} from '@/components/v2/GridLines';
import { useHeaderUI } from '@/components/v2/Header/HeaderUIProvider';
import {
  layoutGridCondensedPadding,
  LayoutSubgrid
} from '@/components/v2/Page/LayoutGrid';
import { styles as headingStyles } from '@/components/v2/UtilityCss/Heading';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { ReactComponent as ChevronRight } from '@/images/icons/icon-chevron-right-medium.svg';

import { Button } from '../design-system';

import { config } from './config';
import { useAnchorContext } from './Context';

const Wrapper = styled(LayoutSubgrid)`
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => theme.palette.fg700};
  scroll-margin-top: calc(var(--workflow-nav-offset, 6rem) + 59px);
`;

const Content = styled.div`
  max-width: 46rem;
  padding: ${layoutGridCondensedPadding};
  grid-column: 2 / span 6;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-column: 2 / -2;
    padding-top: 56px;
    padding-bottom: 32px;
  }
`;

const sectionAnchorCss = css`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  text-transform: uppercase;
  border: 0;

  &:hover {
    background: transparent;
  }

  &[data-notebooks] {
    color: ${config.workflows.notebooks.color};
  }

  &[data-apps] {
    color: ${config.workflows.data_apps.color};
  }

  &[data-self-serve] {
    color: ${config.workflows.self_serve.color};
  }
`;

const SectionAnchorText = styled.span`
  ${sectionAnchorCss}
`;

const SectionAnchor = styled(LinkWithQuery)`
  ${sectionAnchorCss}
`;

const H2 = styled.h2`
  text-wrap: balance;
  margin-bottom: 8px;
`;

const P = styled.p`
  max-width: 40ch;
`;

const Em = styled.em`
  font-weight: ${({ theme }) => theme.fontWeight.BOLD};
  color: ${({ theme }) => theme.palette.fgLoud};
`;

const AccentPlus = styled.div`
  position: absolute;
  color: ${({ theme }) => theme.palette.fg1200};
  top: 50%;
  left: 50%;
  height: 6px;
  width: 6px;
  display: grid;
  align-items: center;
  justify-items: center;
  justify-content: center;
  place-items: center;
  z-index: 1;
  display: none;
  transform: translate(-50%, -50%);

  &::before,
  &::after {
    content: '';
    display: block;
    background-color: currentColor;
    grid-area: 1 / -1;
  }

  &::before {
    width: 1px;
    height: 6px;
  }

  &::after {
    width: 6px;
    height: 1px;
  }
`;

const Selector = styled.fieldset`
  margin-top: 16px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-top: 64px;
  }
`;

const Radio = styled.input`
  opacity: 0;
`;

const SelectorTitle = styled.span`
  display: block;
  color: ${({ theme }) => rgba(theme.palette.fgLoud, 0.32)};
  transition: color 0.08s;
  max-width: 26rem;
  text-wrap: balance;
`;

const SelectorContainer = styled.span`
  position: relative;
  display: block;
  padding: 16px 32px;
  border-left: 1px solid ${({ theme }) => rgba(theme.palette.fgLoud, 0.24)};

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 16px;
    padding-right: 0;
  }

  &::before,
  &::after {
    content: '';
    opacity: 0;
    position: absolute;
    inset: 0 auto 0 -1px;
    width: 1px;
    background-color: ${({ theme }) => rgba(theme.palette.fgLoud, 0.24)};
  }

  &::after {
    transform: scaleY(var(--progress, 0));
    transform-origin: top center;
    background-color: var(
      --radio-group-selection-color,
      ${({ theme }) => theme.palette.fgLoud}
    );

    box-shadow: 0 0 8px 0.5px
      var(
        --radio-group-selection-glow-color,
        ${({ theme }) => theme.palette.fgLoud}
      );
  }
`;

const Expander = styled.span`
  --swift-out: cubic-bezier(0.175, 0.885, 0.32, 1.1);
  --in-out-base: cubic-bezier(0.25, 0.1, 0.25, 1);

  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 0.28s var(--in-out-base);
`;

const ExpanderContent = styled.span`
  overflow: hidden;
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 0.6s var(--in-out-base),
    transform 0.28s var(--in-out-base);
`;

const Label = styled.label`
  display: grid;

  &:hover ${SelectorTitle} {
    color: ${({ theme }) => rgba(theme.palette.fgLoud, 0.72)};
  }

  & input:checked + ${SelectorContainer} {
    &::before,
    &::after {
      opacity: 1;
    }

    ${SelectorTitle} {
      color: ${({ theme }) => rgba(theme.palette.fgLoud, 1.0)};
    }
  }

  &:has(input:checked) ${Expander} {
    grid-template-rows: 1fr;

    ${ExpanderContent} {
      opacity: 1;
      transform: none;
    }
  }

  & > * {
    grid-area: 1 / -1;
  }
`;

function Option({
  title,
  children,
  group,
  checked,
  value,
  onChange,
  isInView,
  onAnimationComplete,
  disableAutoplay
}: PropsWithChildren<{
  checked?: boolean;
  disableAutoplay?: boolean;
  group: string;
  isInView: boolean;
  onAnimationComplete: () => void;
  onChange: ComponentProps<typeof Radio>['onChange'];
  title: string;
  value: number;
}>) {
  const ref = useRef<HTMLLabelElement>(null);
  useEffect(() => {
    if (disableAutoplay) return;
    const node = ref.current;

    if (!node) return;
    if (!isInView) return;
    if (!checked) return;

    const animation = animate(
      node,
      { '--progress': [0, 1] },
      {
        duration: 6,
        ease: 'linear',
        onComplete: () => {
          onAnimationComplete();
        }
      }
    );

    return () => {
      animation.cancel();
    };
  }, [isInView, checked, onAnimationComplete, disableAutoplay]);

  const disabledAutoplayStyles = { '--progress': checked ? 1 : 0 };

  return (
    <Label ref={ref} style={disableAutoplay ? disabledAutoplayStyles : {}}>
      <Radio
        checked={checked}
        name={group + '-radio-group'}
        type="radio"
        value={value}
        onChange={onChange}
      />
      <SelectorContainer>
        <SelectorTitle className={headingStyles.h4}>{title}</SelectorTitle>
        <Expander>
          <ExpanderContent>{children}</ExpanderContent>
        </Expander>
      </SelectorContainer>
    </Label>
  );
}

const StyledCheckeredMargin = styled(CheckeredMargin)`
  border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
`;

export function WorkflowWrapper({
  children,
  id,
  hideGridLines,
  className
}: PropsWithChildren<{
  className?: string;
  hideGridLines?: boolean;
  id: string;
}>) {
  const { navHeight, gridlineHeight } = useHeaderUI();
  const target = useRef<HTMLDivElement>(null);
  const isInView = useInView(target, {
    once: false,
    margin: '-49% 0% -49% 0%',
    amount: 'some'
  });
  const { setAnchor } = useAnchorContext();

  useEffect(() => {
    if (isInView) setAnchor(id);
  }, [isInView, setAnchor, id]);

  return (
    <>
      {hideGridLines ? null : <StyledCheckeredMargin />}
      <LayoutSubgrid
        {...(hideGridLines ? {} : { 'data-content-grid': true })}
        style={{ zIndex: 0 }}>
        {hideGridLines ? null : (
          <LayoutSubgrid data-content-grid data-pile>
            <GridRule $column={6}>
              <AccentPlus />
            </GridRule>
          </LayoutSubgrid>
        )}
        <Wrapper
          ref={target}
          className={className}
          {...(hideGridLines
            ? {}
            : { 'data-content-grid': true, 'data-pile': true })}
          id={id}
          style={{
            '--workflow-nav-offset': navHeight + gridlineHeight + 'px'
          }}>
          {children}
        </Wrapper>
      </LayoutSubgrid>
      {hideGridLines ? null : <StyledCheckeredMargin data-right />}
    </>
  );
}

export function WorkflowSectionAnchor({
  children,
  ...props
}: ComponentProps<typeof SectionAnchor>) {
  return (
    <SectionAnchor {...props}>
      {children} <ChevronRight />
    </SectionAnchor>
  );
}

export function WorkflowSectionAnchorText({
  children,
  ...props
}: ComponentProps<typeof SectionAnchorText>) {
  return (
    <SectionAnchorText {...props}>
      {children} <ChevronRight />
    </SectionAnchorText>
  );
}

function Headline(props: ComponentProps<typeof H2>) {
  return (
    <H2
      {...props}
      className={headingStyles.h2 + ' ' + (props.className || '')}
    />
  );
}

function SubHeadline(props: ComponentProps<typeof P>) {
  return (
    <P {...props} className={textStyles.l + ' ' + (props.className || '')} />
  );
}

const WorkflowSelector = forwardRef<
  HTMLFieldSetElement,
  ComponentProps<typeof Selector> & { color: string }
>(function WorkflowSelector({ color, style, ...props }, ref) {
  return (
    <Selector
      ref={ref}
      {...props}
      style={{
        '--radio-group-selection-color': color,
        '--radio-group-selection-glow-color': rgba(color, 0.24),
        ...(style ? style : {})
      }}
    />
  );
});

const OptionText = styled.span`
  display: block;
  max-width: 20rem;
  padding: 8px 0 0 0;
`;

function OptionContent(props: ComponentProps<typeof OptionText>) {
  return (
    <OptionText
      {...props}
      className={textStyles.base + ' ' + (props.className || '')}
    />
  );
}

const IllustrationArea = styled.div`
  grid-column: span 6;
  background-image: ${({ theme }) => backgroundImageHatched(theme)};
  display: grid;
  justify-content: stretch;
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-column: 1 / -1;
    border-top: 1px solid ${({ theme }) => theme.palette.fg700};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    min-height: 50vh;
    min-height: 50svh;
  }
`;

const IllustrationContainer = styled.div`
  grid-column: 7 / -1;
  align-self: stretch;
  position: relative;
  display: grid;
  overflow: clip;

  border-radius: 8px;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  border: 1px solid
    var(--workflow-border-color, ${rgba(config.workflows.notebooks.color, 0.2)});
  box-shadow:
    1px 2px 4px rgba(0, 0, 0, 0.2),
    4px 6px 8px rgba(0, 0, 0, 0.17),
    10px 14px 10px rgba(0, 0, 0, 0.1),
    18px 25px 12px rgba(0, 0, 0, 0.03),
    28px 39px 13px rgba(0, 0, 0, 0);

  margin: 16px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    --fall-off: 64px;
    ${({ theme }) => cssDotGrid(theme)}
    mask-image: linear-gradient(
        to right,
        transparent,
        white var(--fall-off),
        white calc(100% - var(--fall-off)),
        transparent
      ),
      linear-gradient(
        to bottom,
        white,
        transparent var(--fall-off),
        transparent calc(100% - var(--fall-off)),
        white
      );
    mask-composite: subtract;
  }

  &::after {
    content: ' ';
    position: absolute;
    inset: 0;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  }
`;

const IllustrationImg = styled.div<{
  'data-bottom'?: boolean;
  'data-center'?: boolean;
}>`
  display: block;
  position: relative;
  width: 90%;
  height: 90%;
  place-self: center center;
  display: flex;

  &[data-full] {
    width: 100%;
    height: 100%;
  }

  & [data-gatsby-image-wrapper] {
    margin: auto;
  }

  img,
  picture,
  [data-gatsby-image-wrapper],
  [data-main-image] {
    position: absolute;
    object-fit: contain !important;
    object-position: center center !important;
    inset: 0;
  }
`;

const PresenceContainer = styled(motion.div)`
  align-self: stretch;
  position: relative;
  display: grid;
  will-change: transform;
  isolation: isolate;
  z-index: 1;
`;

const IllustrationPresenceAnim = forwardRef<HTMLDivElement, PropsWithChildren>(
  function IllustrationPresenceAnim({ children }, ref) {
    return (
      <PresenceContainer
        ref={ref}
        animate={{ y: 0, opacity: 1 }}
        exit={{ opacity: 0, y: 32 }}
        initial={{ opacity: 0, y: 32 }}
        transition={{ type: 'spring', bounce: 0.1, duration: 0.4 }}>
        {children}
      </PresenceContainer>
    );
  }
);

const OptionButton = styled(Button)`
  margin-top: 16px;
  width: min-content;
  white-space: nowrap;
`;

export const Workflow = {
  Section: WorkflowWrapper,
  Content,
  TagLink: WorkflowSectionAnchor,
  TagLinkText: WorkflowSectionAnchorText,
  Headline,
  SubHeadline,
  Em,
  Selector: WorkflowSelector,
  Option,
  OptionContent,
  OptionButton,
  IllustrationArea,
  IllustrationContainer,
  IllustrationImg,
  IllustrationPresenceAnim
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useIncrementSelection<T extends { value: any }>(opts: T[]) {
  const [state, dispatch] = useReducer(
    (
      state: T['value'],
      action: { type: 'increment' } | { payload: T['value']; type: 'set' }
    ) => {
      switch (action.type) {
        case 'increment': {
          let idx = opts.findIndex(opt => opt.value === state);
          idx += 1;
          return idx >= opts.length ? opts[0].value : opts[idx].value;
        }

        case 'set':
          return action.payload;

        default:
          return state;
      }
    },
    opts[0].value
  );

  const increment = useCallback(() => {
    dispatch({ type: 'increment' });
  }, [dispatch]);

  const set = useCallback(
    (payload: T['value']) => {
      dispatch({ type: 'set', payload });
    },
    [dispatch]
  );

  return { value: state, option: opts[state], increment, set };
}
